import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Pencil, Plus, ShieldCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { API, formatApiErrorDetail, useAuth } from "../context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const emptyForm = { name: "", email: "", password: "", role: "patient", therapist_id: "" };

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const therapists = users.filter((u) => u.role === "therapist");

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/users`);
      setUsers(data);
    } catch {
      toast.error("Benutzer konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (u) => {
    setEditing(u.user_id);
    setForm({
      name: u.name,
      email: u.email,
      password: "",
      role: u.role,
      therapist_id: u.therapist_id || "",
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name und E-Mail sind Pflichtfelder");
      return;
    }
    if (!editing && form.password.length < 6) {
      toast.error("Bitte ein Passwort mit mindestens 6 Zeichen vergeben");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        role: form.role,
        therapist_id: form.role === "patient" ? form.therapist_id : "",
      };
      if (editing) {
        await axios.put(`${API}/admin/users/${editing}`, {
          ...payload,
          password: form.password || null,
        });
      } else {
        await axios.post(`${API}/admin/users`, { ...payload, password: form.password });
      }
      toast.success(editing ? "Benutzer aktualisiert" : "Benutzer angelegt");
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (u) => {
    try {
      await axios.delete(`${API}/admin/users/${u.user_id}`);
      toast.success("Benutzer gelöscht");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const roleBadge = (u) =>
    u.role === "therapist" ? (
      <Badge className="bg-accent text-primary hover:bg-accent" data-testid={`user-role-badge-${u.user_id}`}>
        Therapeut{u.is_admin ? " · Admin" : ""}
      </Badge>
    ) : (
      <Badge variant="outline" className="text-zinc-400" data-testid={`user-role-badge-${u.user_id}`}>
        Patient
      </Badge>
    );

  return (
    <div className="space-y-8" data-testid="admin-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-zinc-400">
            <ShieldCheck className="h-4 w-4 text-primary" /> Admin-Bereich
          </p>
          <h1 className="mt-1 font-heading text-3xl font-bold tracking-tight text-zinc-100 sm:text-4xl">
            Benutzerverwaltung
          </h1>
          <p className="mt-2 text-base text-zinc-400">
            Patienten und Therapeuten anlegen, bearbeiten und löschen.
          </p>
        </div>
        <Button onClick={openCreate} className="bg-primary hover:bg-primary/90" data-testid="new-user-button">
          <Plus className="mr-2 h-4 w-4" />
          Neuer Benutzer
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20" data-testid="admin-users-loading">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-zinc-800 bg-zinc-900 shadow-sm" data-testid="admin-users-table">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>E-Mail</TableHead>
                <TableHead>Rolle</TableHead>
                <TableHead>Therapeut</TableHead>
                <TableHead className="text-right">Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.user_id} data-testid={`admin-user-row-${u.user_id}`}>
                  <TableCell className="font-medium text-zinc-100">{u.name}</TableCell>
                  <TableCell className="text-zinc-400">{u.email}</TableCell>
                  <TableCell>{roleBadge(u)}</TableCell>
                  <TableCell className="text-zinc-400">
                    {u.role === "patient" ? u.therapist_name || "—" : "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEdit(u)}
                      data-testid={`edit-user-button-${u.user_id}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    {u.user_id !== me?.user_id && !u.is_admin && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-zinc-500 hover:text-destructive"
                            data-testid={`delete-user-button-${u.user_id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent data-testid={`delete-user-dialog-${u.user_id}`}>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Benutzer wirklich löschen?</AlertDialogTitle>
                            <AlertDialogDescription>
                              {u.name} ({u.email}) wird unwiderruflich gelöscht.
                              {u.role === "therapist"
                                ? " Zugeordnete Patienten werden nicht gelöscht, sondern als „ohne Therapeut“ markiert."
                                : " Alle Trainingspläne und der Fortschritt dieses Patienten werden ebenfalls gelöscht."}
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel data-testid={`cancel-delete-user-${u.user_id}`}>
                              Abbrechen
                            </AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => remove(u)}
                              className="bg-destructive text-white hover:bg-destructive/90"
                              data-testid={`confirm-delete-user-${u.user_id}`}
                            >
                              Endgültig löschen
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="user-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Benutzer bearbeiten" : "Neuen Benutzer anlegen"}</DialogTitle>
            <DialogDescription>
              {editing
                ? "Daten anpassen. Passwort nur ausfüllen, wenn es geändert werden soll."
                : "Lege einen Patienten oder Therapeuten direkt an."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4" data-testid="user-form">
            <div className="space-y-2">
              <Label htmlFor="uf-name">Name</Label>
              <Input
                id="uf-name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Max Mustermann"
                data-testid="user-form-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="uf-email">E-Mail</Label>
              <Input
                id="uf-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="name@beispiel.de"
                data-testid="user-form-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="uf-password">
                Passwort{" "}
                {editing && <span className="font-normal text-zinc-500">(leer = unverändert)</span>}
              </Label>
              <Input
                id="uf-password"
                type="password"
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                placeholder="Mindestens 6 Zeichen"
                data-testid="user-form-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Rolle</Label>
              <Select
                value={form.role}
                onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}
                disabled={editing === me?.user_id}
              >
                <SelectTrigger data-testid="user-form-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="patient" data-testid="user-form-role-patient">
                    Patient
                  </SelectItem>
                  <SelectItem value="therapist" data-testid="user-form-role-therapist">
                    Therapeut
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.role === "patient" && (
              <div className="space-y-2">
                <Label>Zugeordneter Therapeut</Label>
                <Select
                  value={form.therapist_id}
                  onValueChange={(v) => setForm((f) => ({ ...f, therapist_id: v }))}
                >
                  <SelectTrigger data-testid="user-form-therapist-select">
                    <SelectValue placeholder="Kein Therapeut zugeordnet" />
                  </SelectTrigger>
                  <SelectContent>
                    {therapists.map((t) => (
                      <SelectItem
                        key={t.user_id}
                        value={t.user_id}
                        data-testid={`user-form-therapist-option-${t.user_id}`}
                      >
                        {t.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <Button
              onClick={save}
              disabled={saving}
              className="w-full bg-primary hover:bg-primary/90"
              data-testid="user-form-save-button"
            >
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editing ? "Änderungen speichern" : "Benutzer anlegen"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
